import { AxiosError } from 'axios';

export type HttpErrorType =
  | 'network'
  | 'timeout'
  | 'forbidden'
  | 'notFound'
  | 'conflict'
  | 'validation'
  | 'server';

/**
 * Classifies an unknown error into a typed HttpErrorType.
 *
 * @remarks
 * - Network: no response received (offline, CORS, DNS failure)
 * - Timeout: request exceeded the configured timeout
 * - 403 → forbidden, 404 → notFound, 409 → conflict
 * - 400 / 422 → validation
 * - Everything else with a response → server
 */
/**
 * Extracts the backend error code from a ProblemDetails response.
 * Reads the `type` field first (e.g. "Area.NotFound"), falling back to `title`
 * — the calendar integration endpoints emit the stable code there (spec §2.6).
 * Returns null when neither is present.
 */
export function extractBackendErrorCode(error: unknown): string | null {
  if (!(error instanceof AxiosError)) return null;
  const data = error.response?.data as Record<string, unknown> | undefined;
  const type = data?.type;
  if (typeof type === 'string' && type.length > 0) return type;
  const title = data?.title;
  return typeof title === 'string' && title.length > 0 ? title : null;
}

export function classifyHttpError(error: unknown): HttpErrorType {
  if (!(error instanceof AxiosError)) return 'server';

  if (error.code === 'ECONNABORTED' || error.code === 'ERR_CANCELED') return 'timeout';
  if (!error.response) return 'network';

  const status = error.response.status;

  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status === 409) return 'conflict';
  if (status === 400 || status === 422) return 'validation';

  return 'server';
}
